import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react'

/**
 * Componentes base. Tokens de cor vêm do CSS da aplicação (@theme do Tailwind):
 *   navy   — marca, cabeçalho, texto forte
 *   acao   — laranja #FF6600: SÓ ação e destaque, nunca "valor ruim"
 *   alerta — variação negativa / erro (cor semântica, não a da marca)
 */

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ')

type Variante = 'primario' | 'secundario' | 'fantasma' | 'perigo'

export function Botao({
  variante = 'secundario',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante }) {
  return (
    <button
      {...props}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium transition',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acao',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variante === 'primario' && 'bg-acao text-white hover:bg-acao-forte',
        variante === 'secundario' && 'border border-linha bg-painel text-tinta hover:bg-fundo',
        variante === 'fantasma' && 'text-tinta-2 hover:bg-fundo hover:text-tinta',
        variante === 'perigo' && 'border border-alerta/40 bg-painel text-alerta hover:bg-alerta/5',
        className,
      )}
    />
  )
}

export function Campo({ rotulo, dica, children }: { rotulo: string; dica?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium tracking-wide text-tinta-2">{rotulo}</span>
      {children}
      {dica && <span className="mt-1 block text-xs text-tinta-3">{dica}</span>}
    </label>
  )
}

const baseEntrada =
  'w-full rounded-md border border-linha bg-painel px-3 py-2 text-sm text-tinta placeholder:text-tinta-3 focus:border-navy focus:outline-none focus:ring-2 focus:ring-navy/15 disabled:bg-fundo'

export function Entrada({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(baseEntrada, className)} />
}

export function Selecao({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={cx(baseEntrada, 'pr-8', className)}>
      {children}
    </select>
  )
}

export function Aviso({ tom = 'alerta', children }: { tom?: 'alerta' | 'info' | 'ok'; children: ReactNode }) {
  return (
    <div
      role={tom === 'alerta' ? 'alert' : 'status'}
      className={cx(
        'rounded-md border px-3.5 py-2.5 text-sm',
        tom === 'alerta' && 'border-alerta/30 bg-alerta/5 text-alerta',
        tom === 'info' && 'border-navy/20 bg-navy/5 text-navy',
        tom === 'ok' && 'border-ok/30 bg-ok/5 text-ok',
      )}
    >
      {children}
    </div>
  )
}

export function Cartao({ titulo, acoes, children, className }: {
  titulo?: ReactNode
  acoes?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cx('rounded-lg border border-linha bg-painel', className)}>
      {(titulo || acoes) && (
        <header className="flex items-center justify-between gap-4 border-b border-linha px-5 py-3">
          <h2 className="text-sm font-semibold text-tinta">{titulo}</h2>
          {acoes}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  )
}

export function Selo({ tom = 'neutro', children }: { tom?: 'neutro' | 'ok' | 'alerta' | 'navy'; children: ReactNode }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium',
        tom === 'neutro' && 'bg-fundo text-tinta-2',
        tom === 'ok' && 'bg-ok/10 text-ok',
        tom === 'alerta' && 'bg-alerta/10 text-alerta',
        tom === 'navy' && 'bg-navy/10 text-navy',
      )}
    >
      {children}
    </span>
  )
}

/** Estado vazio que ensina o próximo passo — nunca ilustração decorativa (§12.4). */
export function EstadoVazio({ titulo, children, acao }: { titulo: string; children: ReactNode; acao?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-linha px-6 py-10 text-center">
      <p className="text-sm font-semibold text-tinta">{titulo}</p>
      <div className="mx-auto mt-2 max-w-md text-sm text-tinta-2">{children}</div>
      {acao && <div className="mt-5">{acao}</div>}
    </div>
  )
}
